import {
  ATTENDANCE_MUTATION_OPERATION_IDS,
  ATTENDANCE_QUERY_OPERATION_IDS,
} from "@triedge/attendance-domain/router-contract";
import {
  ystnjMutationOperationContext,
  ystnjQueryOperationContext,
} from "@triedge/rtk-abstractions";

export const DOMAIN_CONTEXT_TAGS = [
  "AcademicsDomain",
  "AdmissionsDomain",
  "AssistantPlatformDomain",
  "AssistantIntegrationsDomain",
  "AdminGovernanceDomain",
  "AttendanceDomain",
  "CampsDomain",
  "DonationsDomain",
  "FinancialAidDomain",
  "PaymentsDomain",
  "PayrollDomain",
  "SubstitutesDomain",
  "StaffSettingsDomain",
  "ParentPortalDomain",
  "MessagingDomain",
  "NotificationsDomain",
  "StudentsFamiliesDomain",
  "FacultyDomain",
  "TipATeacherDomain",
  "TuitionDomain",
  "DomainCore",
] as const;

export type DomainContextTag = (typeof DOMAIN_CONTEXT_TAGS)[number];

export const DOMAIN_CONTEXT_LABELS: Record<DomainContextTag, string> = {
  AcademicsDomain: "Academics",
  AdmissionsDomain: "Admissions",
  AssistantPlatformDomain: "Assistant Platform",
  AssistantIntegrationsDomain: "Assistant Integrations",
  AdminGovernanceDomain: "Admin Governance",
  AttendanceDomain: "Attendance",
  CampsDomain: "Camps",
  DonationsDomain: "Donations",
  FinancialAidDomain: "Financial Aid",
  PaymentsDomain: "Payments",
  PayrollDomain: "Payroll",
  SubstitutesDomain: "Substitutes",
  StaffSettingsDomain: "Staff Settings",
  ParentPortalDomain: "Parent Portal",
  MessagingDomain: "Messaging",
  NotificationsDomain: "Notifications",
  StudentsFamiliesDomain: "Students & Families",
  FacultyDomain: "Faculty",
  TipATeacherDomain: "Tip a Teacher",
  TuitionDomain: "Tuition",
  DomainCore: "Domain Core",
};

const toDomainContextTag = (value: string): DomainContextTag => {
  if ((DOMAIN_CONTEXT_TAGS as readonly string[]).includes(value)) {
    return value as DomainContextTag;
  }
  return "DomainCore";
};

const mapOperationsToContext = (
  operationIds: readonly string[],
  contextTag: DomainContextTag,
) =>
  Object.fromEntries(
    operationIds.map((operationId) => [operationId, contextTag]),
  );

export const QUERY_OPERATION_CONTEXT = Object.freeze({
  ...Object.fromEntries(
    Object.entries(ystnjQueryOperationContext).map(
      ([operationId, contextTag]) => [
        operationId,
        toDomainContextTag(String(contextTag || "").trim()),
      ],
    ),
  ),
  listTimelineEvents: "AdmissionsDomain",
  listPendingFinancialAidAppeals: "FinancialAidDomain",
  getFinancialAidOpenCycle: "FinancialAidDomain",
  lookupFinancialAidFamily: "FinancialAidDomain",
  listFinancialAidApplicationReportArtifacts: "FinancialAidDomain",
  // INT-568: parent dashboard card and shell access gate read this list.
  // Without the FinancialAidDomain tag, draft saves (update/claim/create)
  // never invalidate it, so the dashboard shows a stale empty list until a
  // manual refresh.
  getFinancialAidByApplicant: "FinancialAidDomain",
  listMessageThreads: "MessagingDomain",
  getMessageThread: "MessagingDomain",
  ...mapOperationsToContext(ATTENDANCE_QUERY_OPERATION_IDS, "AttendanceDomain"),
  previewAssistantReportGeneration: "AssistantPlatformDomain",
  getAssistantReportGenerationWorkspace: "AssistantPlatformDomain",
  listAssistantReportTemplates: "AssistantPlatformDomain",
  listAssistantReportSchedules: "AssistantPlatformDomain",
  listAssistantWorkflowTemplates: "AssistantPlatformDomain",
  listAssistantWorkflows: "AssistantPlatformDomain",
  getAssistantWorkflow: "AssistantPlatformDomain",
  getAssistantWorkflowStudioState: "AssistantPlatformDomain",
  listAssistantWorkflowExecutions: "AssistantPlatformDomain",
  getAssistantWorkflowExecution: "AssistantPlatformDomain",
  getAssistantSpreadsheetBridgeStatus: "AssistantIntegrationsDomain",
  getSubstituteDashboard: "SubstitutesDomain",
  listOpenAbsences: "SubstitutesDomain",
  listSubstitutePool: "SubstitutesDomain",
  listSubstituteSchedule: "SubstitutesDomain",
  getSubstitutePayroll: "SubstitutesDomain",
  getImpersonationSession: "AdminGovernanceDomain",
  getViewAsContext: "AdminGovernanceDomain",
  listTenantSchools: "AdminGovernanceDomain",
  getActiveSchoolContext: "AdminGovernanceDomain",
  getTipParentCatalog: "TipATeacherDomain",
  listTipParentHistory: "TipATeacherDomain",
  getTipSchoolOverview: "TipATeacherDomain",
  getTipProgramSettings: "TipATeacherDomain",
  listTipNotifications: "TipATeacherDomain",
  getDonationFundingConfig: "PaymentsDomain",
  listPendingDonationSettlements: "PaymentsDomain",
  getSchoolTuitionDashboardTree: "StudentsFamiliesDomain",
  getSchoolTuitionFamilyLedgerTree: "StudentsFamiliesDomain",
  resolveFamilyForAdmissionsApplication: "AdmissionsDomain",
  getAdmissionsTuitionConfiguration: "AdmissionsDomain",
  getApplicationDocumentDownloadUrl: "AdmissionsDomain",
  getParentAdmissionsDraft: "AdmissionsDomain",
  getStudentsDashboardTree: "StudentsFamiliesDomain",
  getFamilyDetailCoreTree: "StudentsFamiliesDomain",
  getFamilyDetailTuitionTree: "StudentsFamiliesDomain",
  getFamilyDetailDocumentsTree: "StudentsFamiliesDomain",
  getFamilyDetailNotesTree: "StudentsFamiliesDomain",
  getAdHocInvoice: "StudentsFamiliesDomain",
  listAdHocInvoices: "StudentsFamiliesDomain",
  getBulkChargeJob: "StudentsFamiliesDomain",
  previewBulkChargeSegment: "StudentsFamiliesDomain",
  getRecurringFeeDefinition: "StudentsFamiliesDomain",
  listRecurringFeeDefinitions: "StudentsFamiliesDomain",
  listRecurringFeeAssignments: "StudentsFamiliesDomain",
  // RTK Glue seams — StaffSettings governance (backend by AppSync expert)
  listModuleEnablementAuditEvents: "StaffSettingsDomain",
  listAdministrators: "StaffSettingsDomain",
  listStaffCodes: "StaffSettingsDomain",
  listPendingStaffRequests: "StaffSettingsDomain",
  listApprovedDomains: "StaffSettingsDomain",
  // RTK Glue seam — administrator tuition dashboard (backend by AppSync expert)
  getAdministratorTuitionDashboard: "TuitionDomain",
}) as Readonly<Record<string, DomainContextTag>>;

export type DomainRouterKnownQueryName = keyof typeof QUERY_OPERATION_CONTEXT;

export const MUTATION_OPERATION_CONTEXT = Object.freeze({
  ...Object.fromEntries(
    Object.entries(ystnjMutationOperationContext).map(
      ([operationId, contextTag]) => [
        operationId,
        toDomainContextTag(String(contextTag || "").trim()),
      ],
    ),
  ),
  sendMessage: "MessagingDomain",
  mutateAssistantReportTemplate: "AssistantPlatformDomain",
  mutateAssistantReportSchedule: "AssistantPlatformDomain",
  upsertAssistantWorkflowDraft: "AssistantPlatformDomain",
  submitAssistantWorkflowForApproval: "AssistantPlatformDomain",
  reviewAssistantWorkflow: "AssistantPlatformDomain",
  startAssistantSpreadsheetBridgeAuth: "AssistantIntegrationsDomain",
  completeAssistantSpreadsheetBridgeAuth: "AssistantIntegrationsDomain",
  exportAssistantSpreadsheetToGoogleDrive: "AssistantIntegrationsDomain",
  ...mapOperationsToContext(
    ATTENDANCE_MUTATION_OPERATION_IDS,
    "AttendanceDomain",
  ),
  assignSubstitute: "SubstitutesDomain",
  completeStripeConnectedAccountOnboarding: "PaymentsDomain",
  confirmFinancialAidDocumentUpload: "FinancialAidDomain",
  // INT-568: parent draft creation and submission must invalidate the
  // FinancialAidDomain caches (dashboard card, shell gate) like the other
  // financial aid mutations already do.
  createFinancialAidApplication: "FinancialAidDomain",
  submitFinancialAidApplication: "FinancialAidDomain",
  beginFinancialAidReview: "FinancialAidDomain",
  makeFinancialAidDecision: "FinancialAidDomain",
  requestFinancialAidMoreInfo: "FinancialAidDomain",
  assignFinancialAidApplication: "FinancialAidDomain",
  updateFinancialAidApplication: "FinancialAidDomain",
  inviteFinancialAidApplication: "FinancialAidDomain",
  copyFinancialAidInviteLink: "FinancialAidDomain",
  resendFinancialAidInvite: "FinancialAidDomain",
  recordFinancialAidInviteOpen: "FinancialAidDomain",
  claimFinancialAidInvite: "FinancialAidDomain",
  generateFinancialAidApplicationReport: "FinancialAidDomain",
  getFinancialAidApplicationReportDownloadUrl: "FinancialAidDomain",
  createDonationDafRecommendation: "PaymentsDomain",
  voidDonationDafRecommendation: "PaymentsDomain",
  applyDonationSettlementObservation: "PaymentsDomain",
  updateTipProgramSettings: "TipATeacherDomain",
  sendTipNotificationTest: "TipATeacherDomain",
  upsertAdmissionsTuitionConfiguration: "AdmissionsDomain",
  generateTuitionContract: "AdmissionsDomain",
  sendTuitionContract: "AdmissionsDomain",
  upsertParentAdmissionsDraft: "AdmissionsDomain",
  deleteParentAdmissionsDraft: "AdmissionsDomain",
  reverseTuitionPayment: "StudentsFamiliesDomain",
  issueTuitionRefund: "StudentsFamiliesDomain",
  createAdHocCharge: "StudentsFamiliesDomain",
  createBulkCharge: "StudentsFamiliesDomain",
  createRecurringFeeDefinition: "StudentsFamiliesDomain",
  updateRecurringFeeDefinition: "StudentsFamiliesDomain",
  deleteRecurringFeeDefinition: "StudentsFamiliesDomain",
  assignRecurringFee: "StudentsFamiliesDomain",
  unassignRecurringFee: "StudentsFamiliesDomain",
  bulkAssignRecurringFee: "StudentsFamiliesDomain",
  // RTK Glue seams — StaffSettings governance (backend by AppSync expert)
  grantAdministrator: "StaffSettingsDomain",
  revokeAdministrator: "StaffSettingsDomain",
  resendStaffInvite: "StaffSettingsDomain",
  deleteStaffUser: "StaffSettingsDomain",
  launchImpersonationSession: "AdminGovernanceDomain",
  clearImpersonationSession: "AdminGovernanceDomain",
  setViewAsContext: "AdminGovernanceDomain",
  clearViewAsContext: "AdminGovernanceDomain",
  switchSchoolContext: "AdminGovernanceDomain",
  reactivateStaffUser: "StaffSettingsDomain",
  approvePendingStaffRequest: "StaffSettingsDomain",
  rejectPendingStaffRequest: "StaffSettingsDomain",
  createStaffCode: "StaffSettingsDomain",
  rotateStaffCode: "StaffSettingsDomain",
  revokeStaffCode: "StaffSettingsDomain",
  addApprovedDomain: "StaffSettingsDomain",
  removeApprovedDomain: "StaffSettingsDomain",
}) as Readonly<Record<string, DomainContextTag>>;

export type DomainRouterKnownMutationName =
  keyof typeof MUTATION_OPERATION_CONTEXT;

export const resolveQueryContext = (
  operation: DomainRouterKnownQueryName | string,
): DomainContextTag => {
  const key = String(operation || "").trim();
  return key ? QUERY_OPERATION_CONTEXT[key] || "DomainCore" : "DomainCore";
};

export const resolveMutationContext = (
  operation: DomainRouterKnownMutationName | string,
): DomainContextTag => {
  const key = String(operation || "").trim();
  return key ? MUTATION_OPERATION_CONTEXT[key] || "DomainCore" : "DomainCore";
};

export type QueryOperationByContext<Context extends DomainContextTag> = {
  [Operation in DomainRouterKnownQueryName]: (typeof QUERY_OPERATION_CONTEXT)[Operation] extends Context
    ? Operation
    : never;
}[DomainRouterKnownQueryName];

export type MutationOperationByContext<Context extends DomainContextTag> = {
  [Operation in DomainRouterKnownMutationName]: (typeof MUTATION_OPERATION_CONTEXT)[Operation] extends Context
    ? Operation
    : never;
}[DomainRouterKnownMutationName];
